import {EventConsts} from './trigger-events';
import {InteractionTypeConsts} from '@packages/systems/ix2/shared-constants';

const {
  MOUSE_CLICK_INTERACTION,
  MOUSE_HOVER_INTERACTION,
  MOUSE_MOVE_INTERACTION,
  SCROLL_INTO_VIEW_INTERACTION,
  SCROLLING_IN_VIEW_INTERACTION,
  MOUSE_MOVE_IN_VIEWPORT_INTERACTION,
  PAGE_IS_SCROLLING_INTERACTION,
  PAGE_LOAD_INTERACTION,
  PAGE_SCROLLED_INTERACTION,
  NAVBAR_INTERACTION,
  DROPDOWN_INTERACTION,
  ECOMMERCE_CART_INTERACTION,
  TAB_INTERACTION,
  SLIDER_INTERACTION,
} = InteractionTypeConsts;

export type InteractionConstMeta = {
  id: string;
  label: string;
  continuous?: boolean;
  description: string;
  note: string;
  headings: [string] | [string, string];
  isPage?: boolean;
  componentType?: 'navbar' | 'dropdown' | 'ecommerce' | 'tab' | 'slider';
};

const createInteractionConst = ({
  id,
  label,
  continuous = false,
  description,
  note,
  headings,
  isPage = false,
  componentType,
}: InteractionConstMeta): InteractionConstMeta => ({
  id,
  label,
  continuous,
  description,
  note,
  headings,
  isPage,
  componentType,
});

/**
 * General UI  + Logic
 */

export const InteractionConsts = {
  [MOUSE_CLICK_INTERACTION]: createInteractionConst({
    id: MOUSE_CLICK_INTERACTION,
    label: 'Mouse click (tap)',
    headings: ['On 1st click', 'On 2nd click'],
    note: 'Define actions that occur on 1st or 2nd tap.',
    description: 'Animate on 1st or 2nd click/tap.',
  }),

  [MOUSE_HOVER_INTERACTION]: createInteractionConst({
    id: MOUSE_HOVER_INTERACTION,
    label: 'Mouse hover',
    headings: ['On hover', 'On hover out'],
    note: 'Define actions that occur when cursor hovers over or away from element.',
    description:
      'Animate when cursor hovers over or away from element. No touch support.',
  }),

  [MOUSE_MOVE_INTERACTION]: createInteractionConst({
    id: MOUSE_MOVE_INTERACTION,
    label: 'Mouse move over element',
    continuous: true,
    headings: ['On mouse move'],
    note: 'Define actions that occur when cursor moves over element along X or Y axis.',
    description:
      'Animate while cursor moves over the element along the X or Y axis. No touch support.',
  }),

  [SCROLL_INTO_VIEW_INTERACTION]: createInteractionConst({
    id: SCROLL_INTO_VIEW_INTERACTION,
    label: 'Scroll into view',
    headings: ['When scrolled into view', 'When scrolled out of view'],
    note: 'Define actions that occur when element scrolls into view.',
    description: 'Animate when element scrolls into view.',
  }),

  [SCROLLING_IN_VIEW_INTERACTION]: createInteractionConst({
    id: SCROLLING_IN_VIEW_INTERACTION,
    label: 'While scrolling in view',
    continuous: true,
    headings: ['On scroll'],
    note: 'Define actions that occur when element scrolls through viewport.',
    description: 'Animate while element scrolls through viewport.',
  }),

  [MOUSE_MOVE_IN_VIEWPORT_INTERACTION]: createInteractionConst({
    id: MOUSE_MOVE_IN_VIEWPORT_INTERACTION,
    label: 'Mouse move in viewport',
    continuous: true,
    headings: ['On mouse move'],
    note: 'Define actions that occur while the mouse moves over the viewport along the X or Y axis. No touch support.',
    description:
      'Animate while the mouse moves over the viewport along the X or Y axis. No touch support.',
    isPage: true,
  }),

  [PAGE_IS_SCROLLING_INTERACTION]: createInteractionConst({
    id: PAGE_IS_SCROLLING_INTERACTION,
    label: 'While page is scrolling',
    continuous: true,
    headings: ['On scroll'],
    note: 'Define actions that occur during page scroll.',
    description: 'Animate elements as entire page scrolls from top to bottom.',
    isPage: true,
  }),

  [PAGE_LOAD_INTERACTION]: createInteractionConst({
    id: PAGE_LOAD_INTERACTION,
    label: 'Page load',
    headings: ['When page starts loading', 'When page finishes loading'],
    note: 'Define actions that occur when page starts or finishes loading.',
    description: 'Animate when the page starts or finishes loading.',
    isPage: true,
  }),

  [PAGE_SCROLLED_INTERACTION]: createInteractionConst({
    id: PAGE_SCROLLED_INTERACTION,
    label: 'Page scrolled',
    headings: ['When scrolled up', 'When scrolled down'],
    note: 'Define actions that occur when the page scrolls up or down.',
    description: 'Animate when page scrolls up and/or down.',
    isPage: true,
  }),

  [NAVBAR_INTERACTION]: createInteractionConst({
    id: NAVBAR_INTERACTION,
    label: 'Navbar opens',
    headings: ['Menu opens', 'Menu closes'],
    note: 'Select actions to perform when navbar menu opens and closes.',
    description: 'Animate when navbar menu opens or closes.',
    componentType: 'navbar',
  }),

  [DROPDOWN_INTERACTION]: createInteractionConst({
    id: DROPDOWN_INTERACTION,
    label: 'Dropdown opens',
    headings: ['Menu opens', 'Menu closes'],
    note: 'Define actions to perform when dropdown menu opens and closes.',
    description: 'Animate when dropdown menu opens or closes.',
    componentType: 'dropdown',
  }),

  [ECOMMERCE_CART_INTERACTION]: createInteractionConst({
    id: ECOMMERCE_CART_INTERACTION,
    label: 'Cart opens',
    headings: ['Cart opens', 'Cart closes'],
    note: 'Select actions to perform when cart opens and closes.',
    description: 'Animate when cart opens or closes.',
    componentType: 'ecommerce',
  }),

  [TAB_INTERACTION]: createInteractionConst({
    id: TAB_INTERACTION,
    label: 'Tab change',
    headings: ['Tab in view', 'Tab out of view'],
    note: 'Define actions to perform when a tab is in view (active) and out of view (inactive).',
    description: 'Animate when tab is in view or out of view.',
    componentType: 'tab',
  }),

  [SLIDER_INTERACTION]: createInteractionConst({
    id: SLIDER_INTERACTION,
    label: 'Slider change',
    headings: ['Slider in view', 'Slider out of view'],
    note: 'Define actions to perform when slide comes into view and goes out of view.',
    description: 'Animate when slide is in view or out of view.',
    componentType: 'slider',
  }),
} as const;

export const {InteractionElements, InteractionPages, InteractionComponents} =
  Object.keys(InteractionConsts).reduce<{
    InteractionElements: InteractionConstMeta[];
    InteractionPages: InteractionConstMeta[];
    InteractionComponents: InteractionConstMeta[];
  }>(
    (result, key) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly MOUSE_CLICK_INTERACTION: InteractionConstMeta; readonly MOUSE_HOVER_INTERACTION: InteractionConstMeta; readonly MOUSE_MOVE_INTERACTION: InteractionConstMeta; ... 10 more ...; readonly SLIDER_INTERACTION: InteractionConstMeta; }'.
      const value = InteractionConsts[key];
      if (value.isPage) {
        result.InteractionPages.push(value);
      } else {
        // Disable ecommerce for now since there is no support yet
        if (value.componentType === 'ecommerce') {
          return result;
        }

        if (value.componentType != null) {
          result.InteractionComponents.push(value);
        } else {
          result.InteractionElements.push(value);
        }
      }
      return result;
    },
    {InteractionElements: [], InteractionPages: [], InteractionComponents: []}
  );

export const InteractionToEventsMap = {
  /** Timed Elements */
  [MOUSE_CLICK_INTERACTION]: [
    EventConsts.MOUSE_CLICK,
    EventConsts.MOUSE_SECOND_CLICK,
  ],
  [MOUSE_HOVER_INTERACTION]: [EventConsts.MOUSE_OVER, EventConsts.MOUSE_OUT],
  [SCROLL_INTO_VIEW_INTERACTION]: [
    EventConsts.SCROLL_INTO_VIEW,
    EventConsts.SCROLL_OUT_OF_VIEW,
  ],

  /** Timed Page */
  [PAGE_LOAD_INTERACTION]: [EventConsts.PAGE_START, EventConsts.PAGE_FINISH],
  [PAGE_SCROLLED_INTERACTION]: [
    EventConsts.PAGE_SCROLL_UP,
    EventConsts.PAGE_SCROLL_DOWN,
  ],

  /** Continuous Elements */
  [MOUSE_MOVE_INTERACTION]: [EventConsts.MOUSE_MOVE],
  [SCROLLING_IN_VIEW_INTERACTION]: [EventConsts.SCROLLING_IN_VIEW],

  /** Continuous Page */
  [MOUSE_MOVE_IN_VIEWPORT_INTERACTION]: [EventConsts.MOUSE_MOVE_IN_VIEWPORT],
  [PAGE_IS_SCROLLING_INTERACTION]: [EventConsts.PAGE_SCROLL],

  /** Timed Components */
  [DROPDOWN_INTERACTION]: [
    EventConsts.DROPDOWN_OPEN,
    EventConsts.DROPDOWN_CLOSE,
  ],
  [NAVBAR_INTERACTION]: [EventConsts.NAVBAR_OPEN, EventConsts.NAVBAR_CLOSE],

  [ECOMMERCE_CART_INTERACTION]: [
    EventConsts.ECOMMERCE_CART_OPEN,
    EventConsts.ECOMMERCE_CART_CLOSE,
  ],
  [TAB_INTERACTION]: [EventConsts.TAB_ACTIVE, EventConsts.TAB_INACTIVE],
  [SLIDER_INTERACTION]: [
    EventConsts.SLIDER_ACTIVE,
    EventConsts.SLIDER_INACTIVE,
  ],
} as const;

export const UNNAMED_INTERACTION = 'Unnamed interaction';
